import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { FormBackButton, FormLayoutRenderer, renderField } from '../../../components/generic';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { getLocationReturnPath, useNavigateBack } from '../../../hooks/useNavigateBack';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import {
  getInitialFormData,
  moduleConfig,
  workOrderCreatePlanFields,
  workOrderCreatePlanLayout,
  workOrderCreateScopeFields,
  workOrderCreateScopeLayout,
} from './config';
import type { PmsWorkOrder } from './useData';
import { WorkOrderPaneLabel } from './WorkOrderFormChrome';

type LookupRow = { id: number; code?: string | null; name?: string | null };

type DueJobPrefill = {
  id: number;
  jobCode?: string | null;
  title?: string | null;
  description?: string | null;
  priority?: number | null;
  responsibleRankId?: number | null;
  responsibleRank?: { id?: number; name?: string | null; code?: string | null; abrv?: string | null } | null;
  equipmentId?: number | null;
  equipment?: {
    id?: number;
    equipmentCode?: string | null;
    name?: string | null;
    serialNumber?: string | null;
    vesselId?: number | null;
    vessel?: { id?: number; vesselName?: string | null } | null;
  } | null;
  primarySchedule?: {
    id?: number;
    scheduleType?: { name?: string | null; code?: string | null } | null;
    intervalValue?: number | string | null;
  } | null;
};

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function pickLookup(
  rows: LookupRow[] | { data?: LookupRow[] } | undefined,
  preferredCodes: string[],
): LookupRow | undefined {
  const list = listFrom(rows);
  for (const code of preferredCodes) {
    const hit = list.find((r) => (r.code ?? '').toUpperCase() === code.toUpperCase());
    if (hit) return hit;
  }
  return list[0];
}

function applySearchParamPrefill(base: Record<string, unknown>, searchParams: URLSearchParams) {
  const next = { ...base };
  searchParams.forEach((value, key) => {
    if (key === 'fromDueJob') return;
    const numValue = /^\d+$/.test(value) ? Number(value) : value;
    next[key] = numValue;
  });
  return next;
}

const ALL_CREATE_FIELDS = [...workOrderCreateScopeFields, ...workOrderCreatePlanFields];

const WorkOrderCreateForm: React.FC = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const fromDueJob = searchParams.get('fromDueJob') === '1';
  const jobIdParam = searchParams.get('maintenanceJobId');
  const jobId = jobIdParam && /^\d+$/.test(jobIdParam) ? Number(jobIdParam) : null;

  const { data: statuses, isLoading: loadingStatuses } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-work-order-statuses?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: sources, isLoading: loadingSources } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-work-order-sources?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: approvals, isLoading: loadingApprovals } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-approval-statuses?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: job, isLoading: loadingJob } = useSWR<DueJobPrefill>(
    fromDueJob && jobId != null ? `/pms-maintenance-jobs/${jobId}` : null,
    fetcher,
    { revalidateOnFocus: false },
  );

  const status = useMemo(() => pickLookup(statuses, ['DRAFT', 'PLANNED', 'DUE']), [statuses]);
  const source = useMemo(
    () => pickLookup(sources, fromDueJob ? ['SCHEDULED'] : ['MANUAL', 'SCHEDULED']),
    [sources, fromDueJob],
  );
  const approval = useMemo(() => pickLookup(approvals, ['NOT_REQUIRED', 'PENDING']), [approvals]);

  const defaultsReady =
    status?.id != null &&
    source?.id != null &&
    approval?.id != null &&
    (!fromDueJob || job != null);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [formDataVersion, setFormDataVersion] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const seeded = useRef(false);

  useEffect(() => {
    if (!defaultsReady || seeded.current) return;
    seeded.current = true;

    let initial = applySearchParamPrefill(
      getInitialFormData() as Record<string, unknown>,
      searchParams,
    );

    initial = {
      ...initial,
      statusId: status!.id,
      status,
      sourceTypeId: source!.id,
      sourceType: source,
      approvalStatusId: approval!.id,
      approvalStatus: approval,
    };

    if (fromDueJob && job) {
      const vessel = job.equipment?.vessel ?? undefined;
      const equipment = job.equipment ?? undefined;
      const schedule = job.primarySchedule ?? undefined;
      initial = {
        ...initial,
        vesselId: equipment?.vesselId ?? vessel?.id,
        vessel,
        equipmentId: equipment?.id ?? job.equipmentId ?? undefined,
        equipment,
        maintenanceJobId: job.id,
        maintenanceJob: {
          id: job.id,
          jobCode: job.jobCode,
          title: job.title,
        },
        maintenanceScheduleId: schedule?.id,
        maintenanceSchedule: schedule
          ? {
              id: schedule.id,
              intervalValue: schedule.intervalValue,
              scheduleType: schedule.scheduleType,
            }
          : undefined,
        assignedRankId: job.responsibleRankId ?? undefined,
        assignedRank: job.responsibleRank ?? undefined,
        priority: job.priority != null ? Number(job.priority) : initial.priority ?? 3,
        title: (initial.title as string) || job.title || '',
        description: (initial.description as string) || job.description || '',
        snapshotEquipmentCode:
          (initial.snapshotEquipmentCode as string) || equipment?.equipmentCode || '',
        snapshotEquipmentName: (initial.snapshotEquipmentName as string) || equipment?.name || '',
        snapshotEquipmentSerial:
          (initial.snapshotEquipmentSerial as string) || equipment?.serialNumber || '',
        snapshotJobCode: (initial.snapshotJobCode as string) || job.jobCode || '',
        snapshotJobTitle: (initial.snapshotJobTitle as string) || job.title || '',
      };
    }

    if (!initial.workOrderNumber) {
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      initial.workOrderNumber = `WO-${stamp}-${Math.floor(Math.random() * 900 + 100)}`;
    }

    setFormData(initial);
    formDataRef.current = initial;
    setFormDataVersion((v) => v + 1);
  }, [defaultsReady, fromDueJob, job, status, source, approval, searchParams]);

  const setFieldValue = useCallback((name: string, value: unknown) => {
    let next: unknown = value;
    if (
      typeof value === 'string' &&
      value !== '' &&
      /Id$/.test(name) &&
      /^\d+$/.test(value)
    ) {
      next = Number(value);
    }
    formDataRef.current = { ...formDataRef.current, [name]: next };
    setFormData(formDataRef.current);
    setFormDataVersion((v) => v + 1);
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const nextErr = { ...prev };
      delete nextErr[name];
      return nextErr;
    });
  }, []);

  const setMultipleFieldValues = useCallback((updates: Record<string, unknown>) => {
    formDataRef.current = { ...formDataRef.current, ...updates };
    setFormData(formDataRef.current);
    setTimeout(() => setFormDataVersion((v) => v + 1), 0);
  }, []);

  const getFormData = useCallback(() => formDataRef.current, []);

  const renderFields = useCallback(
    (fields: typeof ALL_CREATE_FIELDS) => {
      const out: Record<string, React.ReactNode> = {};
      fields.forEach((field) => {
        out[field.name] = renderField({
          field,
          value: formDataRef.current[field.name],
          onChange: (name, value) => setFieldValue(name, value),
          setFieldValue,
          setMultipleFieldValues,
          getFormData,
          error: formErrors[field.name],
          formKey: 'wo-create',
          formDataVersion,
          isEdit: false,
          formLayoutVariant: 'compact',
        });
      });
      return out;
    },
    [formDataVersion, formErrors, getFormData, setFieldValue, setMultipleFieldValues],
  );

  const scopeFields = useMemo(
    () => renderFields(workOrderCreateScopeFields),
    [renderFields],
  );
  const planFields = useMemo(
    () => renderFields(workOrderCreatePlanFields),
    [renderFields],
  );

  const validate = useCallback(() => {
    const next: Record<string, string> = {};
    ALL_CREATE_FIELDS.forEach((field) => {
      const value = formDataRef.current[field.name];
      const required =
        field.required ||
        (typeof field.validation?.requiredWhen === 'function' &&
          field.validation.requiredWhen(formDataRef.current));
      if (required && (value == null || value === '')) {
        next[field.name] = field.validation?.message || `${field.label} is required`;
      }
    });
    setFormErrors(next);
    return Object.keys(next).length === 0;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const created = (await moduleConfig.form.create(
        formDataRef.current as Partial<PmsWorkOrder>,
      )) as PmsWorkOrder;
      showSuccess('Work order created.');
      if (created?.id != null) {
        navigate(moduleConfig.module.route.edit(Number(created.id)), {
          replace: true,
          state: location.state,
        });
      } else {
        goBack();
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : moduleConfig.form.errorMessage;
      setError(message || moduleConfig.form.errorMessage);
      showError(message || moduleConfig.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingStatuses || loadingSources || loadingApprovals || (fromDueJob && loadingJob) || !defaultsReady) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="large" message={moduleConfig.form.loadingText} />
      </div>
    );
  }

  const woNumber =
    formData.workOrderNumber != null && String(formData.workOrderNumber).trim()
      ? String(formData.workOrderNumber)
      : 'New WO';
  const woTitle =
    formData.title != null && String(formData.title).trim()
      ? String(formData.title)
      : 'Untitled work order';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FormBackButton onClick={goBack} className="mb-2 flex-shrink-0" />

      <form
        onSubmit={handleSubmit}
        className={cn(
          'mx-auto flex w-full max-w-[96rem] min-h-0 flex-1 flex-col overflow-hidden rounded-xl border',
          colors.border,
          colors.surface,
        )}
      >
        {error ? (
          <div className="flex-shrink-0 border-b border-red-200 bg-red-50 px-4 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : null}

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-12">
          <aside
            className={cn(
              'flex min-h-0 flex-col border-b lg:col-span-5 lg:border-b-0 lg:border-r',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className={cn('flex-shrink-0 border-b px-5 py-4', colors.border)}>
              <div className="min-w-0">
                <WorkOrderPaneLabel>Reference</WorkOrderPaneLabel>
                <p className={cn('mt-1 font-mono text-sm font-semibold tracking-tight', colors.primaryFg)}>
                  {woNumber}
                </p>
                <h1 className={cn('mt-1 text-lg font-semibold leading-snug', colors.text)}>
                  {woTitle}
                </h1>
              </div>
              <p className={cn('mt-3 text-xs', colors.textSecondary)}>
                {fromDueJob
                  ? 'Prefilled from Due jobs · set scope and snapshot before creating'
                  : 'Set scope and snapshot · becomes read-only history after create'}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 dashboard-scrollbar">
              <FormLayoutRenderer
                layout={workOrderCreateScopeLayout}
                fields={scopeFields}
                formData={formData}
                variant="compact"
              />
            </div>
          </aside>

          <div className="flex min-h-0 flex-col lg:col-span-7">
            <div className={cn('flex-shrink-0 border-b px-5 py-4', colors.border)}>
              <WorkOrderPaneLabel>Plan</WorkOrderPaneLabel>
              <h2 className={cn('mt-1 text-lg font-semibold', colors.text)}>New work order</h2>
              <p className={cn('mt-1 text-sm', colors.textSecondary)}>
                Status, assignment, instructions, and close-out notes.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 dashboard-scrollbar">
              <FormLayoutRenderer
                layout={workOrderCreatePlanLayout}
                fields={planFields}
                formData={formData}
                variant="compact"
              />
            </div>

            <div
              className={cn(
                'flex flex-shrink-0 items-center justify-between gap-3 border-t px-5 py-3',
                colors.border,
                colors.surface,
              )}
            >
              <p className={cn('hidden text-xs sm:block', colors.textMuted)}>
                Left side freezes as the dossier after create.
              </p>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" variant="outline" onClick={goBack}>
                  {moduleConfig.form.cancelText}
                </Button>
                <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                  {moduleConfig.form.new.submitText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WorkOrderCreateForm;
